import { useForm, Link } from "@inertiajs/react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

type RegisterForm = Record<string, any> & {
  user: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    password_confirmation: string;
  };
};

interface RegisterProps {
  errors?: string[];
  old_input?: {
    first_name?: string;
    last_name?: string;
    email?: string;
  };
}

export default function Register({
  errors = [],
  old_input = {},
}: RegisterProps) {
  const {
    data,
    setData,
    post,
    processing,
    errors: formErrors,
  } = useForm<RegisterForm>({
    user: {
      first_name: old_input.first_name || "",
      last_name: old_input.last_name || "",
      email: old_input.email || "",
      password: "",
      password_confirmation: "",
    },
  });

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    post("/register");
  };

  return (
    <AuthLayout title="Create Account" description="Join the racing community">
      <form onSubmit={submit}>
        <div className="flex flex-col gap-6">

          <div className="flex md:flex-row flex-col gap-6">
            <FormField
              id="first_name"
              label="First Name"
              type="text"
              placeholder="Enter your first name"
              value={data.user.first_name}
              onChange={(value) => setData("user.first_name", value)}
              error={formErrors.first_name}
              required
              autoComplete="given-name"
            />

            <FormField
              id="last_name"
              label="Last Name"
              type="text"
              placeholder="Enter your last name"
              value={data.user.last_name}
              onChange={(value) => setData("user.last_name", value)}
              error={formErrors.last_name}
              required
              autoComplete="family-name"
            />
          </div>

          <FormField
            id="email"
            label="Email Address"
            type="email"
            placeholder="your@email.com"
            value={data.user.email}
            onChange={(value) => setData("user.email", value)}
            error={formErrors.email}
            required
            autoComplete="email"
          />

          <FormField
            id="password"
            label="Password"
            type="password"
            placeholder="••••••••"
            value={data.user.password}
            onChange={(value) => setData("user.password", value)}
            error={formErrors.password}
            required
            autoComplete="new-password"
          />

          <FormField
            id="password_confirmation"
            label="Confirm Password"
            type="password"
            placeholder="••••••••"
            value={data.user.password_confirmation}
            onChange={(value) => setData("user.password_confirmation", value)}
            error={formErrors.password_confirmation}
            required
            autoComplete="new-password"
          />

          {errors.length > 0 && (
            <Alert variant="destructive" className="bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">
                  Please fix the following errors:
                </div>
                <ul className="space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-3 pt-2">
            <Button
              type="submit"
              disabled={processing}
              variant="default"
              size="lg"
              className="w-full shadow-xl"
            >
              {processing ? "Creating Account..." : "Create Account"}
            </Button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm">
          <span className="text-gray-600">Already have an account?</span>{" "}
          <Link
            href="/login"
            className="text-orange-600 hover:text-orange-700 font-bold hover:underline"
          >
            Sign In
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}
